/* eslint-disable react-refresh/only-export-components -- shared step primitives mix utility helpers and components */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

export function tintFromAccent(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return `rgba(59,130,246,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function accentVars(accentColor: string, primaryColor?: string): React.CSSProperties {
  const primary = primaryColor ?? accentColor;
  return {
    ['--accent' as string]: accentColor,
    ['--accent-tint' as string]: tintFromAccent(accentColor, 0.05),
    ['--accent-soft' as string]: tintFromAccent(accentColor, 0.12),
    ['--primary' as string]: primary,
    ['--card-shadow' as string]: `0 1px 0 rgba(15,23,42,0.02), 0 14px 32px -16px ${tintFromAccent(accentColor, 0.4)}, 0 4px 12px -6px ${tintFromAccent(accentColor, 0.25)}`,
  } as React.CSSProperties;
}

interface StepHeaderProps {
  step: number;
  label: string;
  accentColor: string;
}

export const StepHeader: React.FC<StepHeaderProps> = ({ step, label, accentColor }) => (
  <div className="flex items-center gap-4">
    <div
      className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ color: accentColor, backgroundColor: tintFromAccent(accentColor, 0.05) }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }} />
      Step {step} · {label}
    </div>
    <div
      className="flex-1 h-px"
      style={{ background: `linear-gradient(90deg, ${tintFromAccent(accentColor, 0.5)}, transparent)` }}
    />
  </div>
);

interface FieldCardProps {
  accentColor: string;
  children: React.ReactNode;
  className?: string;
}

export const FieldCard: React.FC<FieldCardProps> = ({ accentColor: _accentColor, children, className = '' }) => (
  <div
    className={`relative rounded-2xl border border-slate-200/80 overflow-hidden bg-white ${className}`}
    style={{ boxShadow: 'var(--card-shadow)' }}
  >
    <div className="divide-y divide-slate-200/70">{children}</div>
  </div>
);

// CTV-533 / ADR-0012: single error primitive used across every wizard step
// so visual evolution happens in one place. Rose chip with AlertTriangle —
// matches the wizard's "Future payments canceled" banner family.
interface FieldErrorProps {
  message?: string;
  className?: string;
}

export const FieldError: React.FC<FieldErrorProps> = ({ message, className = '' }) => {
  if (!message) return null;
  return (
    <p
      role="alert"
      className={`inline-flex items-start gap-1.5 text-[12px] font-medium text-rose-600 bg-rose-50 px-2 py-1 rounded-md mt-1.5 ${className}`}
    >
      <AlertTriangle className="w-3.5 h-3.5 mt-[1px] flex-shrink-0" aria-hidden />
      <span>{message}</span>
    </p>
  );
};

// Caution counterpart to FieldError. Same chip shape, amber palette, role="status"
// (advisory, not blocking). Used for soft warnings that shouldn't stop the user,
// e.g. the audio-track-not-detected hint from validateAdSpecs.
export const FieldCaution: React.FC<FieldErrorProps> = ({ message, className = '' }) => {
  if (!message) return null;
  return (
    <p
      role="status"
      className={`inline-flex items-start gap-1.5 text-[12px] font-medium text-amber-800 bg-amber-50 px-2 py-1 rounded-md mt-1.5 ${className}`}
    >
      <AlertTriangle className="w-3.5 h-3.5 mt-[1px] flex-shrink-0" aria-hidden />
      <span>{message}</span>
    </p>
  );
};

const inputBase =
  'peer w-full bg-transparent border-0 px-0 pt-7 pb-2 text-[15px] text-slate-900 placeholder-slate-300 caret-[color:var(--accent)] focus:outline-none focus:ring-0';

interface FloatingFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: React.ReactNode;
  prefix?: string;
  children: (inputProps: { id: string; placeholder: string; className: string }) => React.ReactNode;
}

export const FloatingField: React.FC<FloatingFieldProps> = ({
  id,
  label,
  required,
  error,
  hint,
  prefix,
  children,
}) => {
  // The floating label sits over the input's top padding strip. A click there
  // lands on the input above its text line, so the browser drops the caret at
  // the start. Redirect clicks in that strip to a caret-at-the-end focus so the
  // whole field behaves like clicking the text row. Text-row clicks and clicks
  // on the error/hint below are left to native behavior.
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const input = e.currentTarget.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
    if (!input || input.disabled || input.readOnly) return;
    const rect = input.getBoundingClientRect();
    const padTop = parseFloat(getComputedStyle(input).paddingTop) || 0;
    if (e.clientY < rect.top || e.clientY >= rect.top + padTop) return;
    e.preventDefault();
    input.focus();
    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch {
      // Some input types (email, number) reject setSelectionRange; the focus()
      // above is the best we can do for them.
    }
  };

  return (
  <div
    onMouseDown={handleMouseDown}
    className="relative px-5 pt-2 transition-colors focus-within:bg-[color:var(--accent-tint)] group"
  >
    <span
      aria-hidden
      className={
        error
          ? 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-rose-600 opacity-100 transition-opacity'
          : 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-[color:var(--accent)] opacity-0 group-focus-within:opacity-100 transition-opacity'
      }
    />
    {children({
      id,
      placeholder: label,
      className: prefix ? `${inputBase} pl-4` : inputBase,
    })}
    {prefix && (
      <span
        aria-hidden
        className="
          absolute left-5 top-[2.5rem] text-[15px] leading-none text-slate-500
          pointer-events-none select-none opacity-0 transition-opacity duration-150
          peer-focus:opacity-100 peer-[:not(:placeholder-shown)]:opacity-100
        "
      >
        {prefix}
      </span>
    )}
    <label
      htmlFor={id}
      className={`
        absolute left-5 top-3 pointer-events-none select-none
        text-[11px] font-medium tracking-[0.08em] uppercase
        ${error ? 'text-rose-600' : 'text-[color:var(--accent)]'}
      `}
    >
      {label}
      {required && (
        <span className={`ml-1 ${error ? 'text-rose-600' : 'text-[color:var(--accent)]'}`} aria-hidden>
          •
        </span>
      )}
    </label>
    <FieldError message={error} className="mb-2" />
    {hint && <div className="pb-1">{hint}</div>}
  </div>
  );
};

interface StaticFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: React.ReactNode;
  children: (inputProps: { id: string; className: string }) => React.ReactNode;
}

export const StaticField: React.FC<StaticFieldProps> = ({ id, label, required, error, hint, children }) => (
  <div className="relative px-5 py-3 transition-colors focus-within:bg-[color:var(--accent-tint)] group">
    <span
      aria-hidden
      className={
        error
          ? 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-rose-600 opacity-100 transition-opacity'
          : 'absolute left-0 top-3 bottom-3 w-[2px] rounded-r bg-[color:var(--accent)] opacity-0 group-focus-within:opacity-100 transition-opacity'
      }
    />
    <label
      htmlFor={id}
      className={`block text-[11px] font-medium tracking-[0.08em] uppercase mb-1 transition-colors ${
        error ? 'text-rose-600' : 'text-slate-500 group-focus-within:text-[color:var(--accent)]'
      }`}
    >
      {label}
      {required && (
        <span className={`ml-1 ${error ? 'text-rose-600' : 'text-[color:var(--accent)]'}`} aria-hidden>
          •
        </span>
      )}
    </label>
    {children({
      id,
      className:
        'w-full bg-transparent border-0 px-0 py-1 text-[15px] text-slate-900 placeholder-slate-400 caret-[color:var(--accent)] focus:outline-none focus:ring-0',
    })}
    <FieldError message={error} />
    {hint}
  </div>
);

interface SectionHeadingProps {
  children: React.ReactNode;
  required?: boolean;
}

export const SectionHeading: React.FC<SectionHeadingProps> = ({ children, required }) => (
  <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500 mb-3 px-1">
    {children}
    {required && <span className="ml-1 text-[color:var(--accent)]">•</span>}
  </div>
);
